import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import { useLocale, useTranslations } from "next-intl";
import { apiErrorText, readApiErrorResponse, type ErrorTranslator } from "@/lib/api-error";
import { COMMS_LIMITS } from "@/lib/comms-limits";
import { apiFetch } from "@/services/http/api-fetch";

type AiCitation = {
  lessonId: string;
  lessonTitle: string;
};

type AiChatMessage = {
  id: string;
  role: "user" | "assistant";
  content: string;
  citations: AiCitation[];
  confidence: number | null;
  /** COMM-10: refusals arrive as a code; the text is ours, in the reader's language. */
  refusalCode?: string | null;
  /** COMM-9: "fallback" = answered from keywords because the AI service failed. */
  source?: "llm" | "keyword" | "fallback" | "refusal" | null;
  fallback?: boolean;
  createdAt: string;
};

const KNOWN_REFUSALS = new Set(["OUT_OF_SCOPE"]);

type AiTutorPanelProps = {
  courseId: string;
  tenantSlug: string;
  lessonId: string;
  lessonTitle: string;
};

export function AiTutorPanel({ courseId, tenantSlug, lessonId, lessonTitle }: AiTutorPanelProps) {
  const t = useTranslations("aiTutor");
  const tRoot = useTranslations();
  const [messages, setMessages] = useState<AiChatMessage[]>([]);
  const [demoMode, setDemoMode] = useState(false);
  // R3-O5: the persona is a course/tenant setting; without one the original copy is kept.
  const [personaName, setPersonaName] = useState<string | null>(null);
  // Plan 6 S12b: per-language names; the panel follows the reader's language.
  const [personaNames, setPersonaNames] = useState<{ en?: string; hi?: string } | null>(null);
  const locale = useLocale();
  const [draft, setDraft] = useState("");
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [escalatingId, setEscalatingId] = useState<string | null>(null);
  const [escalatedIds, setEscalatedIds] = useState<Record<string, boolean>>({});
  const [error, setError] = useState<string | null>(null);
  // The raw failure; translated at render so the text can name the persona.
  const [loadError, setLoadError] = useState<unknown>(null);
  const [escalateNotice, setEscalateNotice] = useState<string | null>(null);
  const [open, setOpen] = useState(false);
  const persona = (locale === "hi" ? personaNames?.hi : personaNames?.en) ?? personaName ?? null;
  const p = { persona: persona ?? "" };
  // A school with no persona gets the neutral "AI tutor" copy, never "AI avatar of AI tutor".
  const NEUTRAL_KEYS = new Set([
    "title",
    "loading",
    "loadFailed",
    "assistantLabel",
    "escalate",
    "escalating",
    "escalated",
    "escalateSuccess",
    "escalateFailed",
    "providerDisclosureKeyed",
    "providerDisclosureKeyless",
  ]);
  const tp = (key: string) =>
    persona === null && NEUTRAL_KEYS.has(key) ? t(`neutral.${key}`) : t(key, p);
  // apiErrorText's fallback keys (loadFailed, escalateFailed…) also name the persona.
  const tErr = Object.assign(
    (key: string) => {
      const short = key.replace(/^aiTutor\./, "");
      return persona === null && NEUTRAL_KEYS.has(short)
        ? tRoot(`aiTutor.neutral.${short}`)
        : tRoot(key, p);
    },
    {
      has: (key: string) => tRoot.has(key),
    },
  ) as ErrorTranslator;

  useEffect(() => {
    if (!open) return;

    let active = true;
    const controller = new AbortController();
    setLoading(true);
    setError(null);
    setLoadError(null);

    void (async () => {
      try {
        const res = await apiFetch(`/v1/courses/${courseId}/ai/chat`, {
          signal: controller.signal,
        });
        if (!res.ok) throw await readApiErrorResponse(res);
        const json = (await res.json()) as {
          data: {
            messages?: AiChatMessage[];
            demoMode?: boolean;
            personaName?: string | null;
            personaNames?: { en?: string; hi?: string } | null;
          };
        };
        if (!active) return;
        setMessages(json.data.messages ?? []);
        setDemoMode(Boolean(json.data.demoMode));
        setPersonaName(json.data.personaName?.trim() || null);
        setPersonaNames(json.data.personaNames ?? null);
        setLoading(false);
      } catch (err) {
        if (!active || (err as Error)?.name === "AbortError") return;
        setLoadError(err ?? new Error("load failed"));
        setLoading(false);
      }
    })();

    return () => {
      active = false;
      controller.abort();
    };
  }, [courseId, open]);

  async function handleSend(event: React.FormEvent) {
    event.preventDefault();
    const message = draft.trim();
    if (!message || sending) return;

    setSending(true);
    setError(null);
    setEscalateNotice(null);

    try {
      const res = await apiFetch(`/v1/courses/${courseId}/ai/chat`, {
        method: "POST",
        body: JSON.stringify({ message }),
      });
      if (!res.ok) throw await readApiErrorResponse(res);
      const json = (await res.json()) as {
        data: {
          messages?: AiChatMessage[];
          demoMode?: boolean;
          personaName?: string | null;
          personaNames?: { en?: string; hi?: string } | null;
        };
      };
      setMessages(json.data.messages ?? []);
      setDemoMode(Boolean(json.data.demoMode));
      if (json.data.personaName !== undefined) {
        setPersonaName(json.data.personaName?.trim() || null);
        setPersonaNames(json.data.personaNames ?? null);
      }
      setDraft("");
    } catch (err) {
      setError(apiErrorText(err, tErr, "aiTutor.sendFailed", { namespace: "aiTutor.errors" }));
    } finally {
      setSending(false);
    }
  }

  async function handleEscalate(assistantMessage: AiChatMessage) {
    if (escalatingId || escalatedIds[assistantMessage.id]) return;
    setEscalatingId(assistantMessage.id);
    setError(null);
    setEscalateNotice(null);
    try {
      const res = await apiFetch(`/v1/courses/${courseId}/ai/escalate`, {
        method: "POST",
        // COMM-12: the server reads the question and answer from the stored conversation.
        body: JSON.stringify({
          assistantMessageId: assistantMessage.id,
          lessonId,
          lessonTitle,
        }),
      });
      if (!res.ok) throw await readApiErrorResponse(res);
      setEscalatedIds((prev) => ({ ...prev, [assistantMessage.id]: true }));
      setEscalateNotice(tp("escalateSuccess"));
    } catch (err) {
      setError(apiErrorText(err, tErr, "aiTutor.escalateFailed", { namespace: "aiTutor.errors" }));
    } finally {
      setEscalatingId(null);
    }
  }

  return (
    <section
      className="rounded-xl border border-[var(--border)] bg-[var(--surface-1)] p-5"
      data-testid="ai-tutor-panel"
    >
      <div className="flex items-center justify-between gap-3">
        <div>
          <h2 className="text-lg font-medium">{tp("title")}</h2>
          <p className="text-sm text-[var(--text-secondary)]">{t("subtitle")}</p>
          {demoMode && open ? (
            <p className="mt-1 text-xs text-[var(--text-heading)]" data-testid="ai-tutor-demo-mode">
              {t("demoMode")}
            </p>
          ) : null}
        </div>
        <button
          type="button"
          className="rounded border border-[var(--border-strong)] px-3 py-1.5 text-sm"
          onClick={() => setOpen((value) => !value)}
          data-testid="ai-tutor-toggle"
        >
          {open ? t("hide") : t("show")}
        </button>
      </div>

      {open ? (
        <>
          {loading ? (
            <p className="mt-3 text-sm text-[var(--text-muted)]">{tp("loading")}</p>
          ) : loadError ? (
            <p
              className="mt-3 text-sm text-[var(--text-secondary)]"
              data-testid="ai-tutor-unavailable"
            >
              {apiErrorText(loadError, tErr, "aiTutor.loadFailed", { namespace: "aiTutor.errors" })}
            </p>
          ) : (
            <>
              <p
                className="mt-3 rounded-md border border-[var(--accent-border)] bg-[var(--accent-soft)] px-3 py-2 text-xs text-[var(--text-secondary)]"
                data-testid="ai-tutor-provider-disclosure"
              >
                {demoMode ? tp("providerDisclosureKeyless") : tp("providerDisclosureKeyed")}{" "}
                <Link
                  to={`/t/${tenantSlug}/settings/privacy`}
                  className="underline underline-offset-2"
                  data-testid="ai-tutor-privacy-link"
                >
                  {t("providerDisclosureLink")}
                </Link>
              </p>

              {error ? (
                <p
                  role="alert"
                  className="mt-3 text-sm text-[var(--danger)]"
                  data-testid="ai-tutor-error"
                >
                  {error}
                </p>
              ) : null}
              {escalateNotice ? (
                <p className="mt-3 text-sm text-green-700" data-testid="ai-tutor-escalate-success">
                  {escalateNotice}{" "}
                  <Link
                    to={`/t/${tenantSlug}/messages`}
                    className="underline"
                    data-testid="ai-tutor-escalate-messages-link"
                  >
                    {t("openMessages")}
                  </Link>
                </p>
              ) : null}

              <div
                className="mt-4 max-h-72 space-y-3 overflow-y-auto rounded border border-[var(--border)] p-3"
                data-testid="ai-tutor-messages"
              >
                {messages.length === 0 ? (
                  <p className="text-sm text-[var(--text-muted)]">{t("empty")}</p>
                ) : (
                  messages.map((message, index) => {
                    const priorUser =
                      message.role === "assistant"
                        ? [...messages.slice(0, index)].reverse().find((m) => m.role === "user")
                        : null;
                    return (
                      <div
                        key={message.id}
                        className="text-sm"
                        data-testid={`ai-tutor-message-${message.role}`}
                      >
                        <p className="font-medium">
                          {message.role === "assistant" ? tp("assistantLabel") : t("youLabel")}
                        </p>
                        {message.refusalCode ? (
                          <p className="mt-1 whitespace-pre-wrap" data-testid="ai-tutor-refusal">
                            {t(
                              `refusals.${KNOWN_REFUSALS.has(message.refusalCode) ? message.refusalCode : "OUT_OF_SCOPE"}`,
                            )}
                          </p>
                        ) : (
                          <p className="mt-1 whitespace-pre-wrap">{message.content}</p>
                        )}
                        {message.role === "assistant" && message.fallback ? (
                          <p
                            className="mt-1 text-xs text-[var(--text-muted)]"
                            data-testid="ai-tutor-fallback-label"
                          >
                            {t("fallbackLabel")}
                          </p>
                        ) : null}
                        {message.citations.length > 0 ? (
                          <ul
                            className="mt-2 text-xs text-[var(--text-secondary)]"
                            data-testid="ai-tutor-citations"
                          >
                            {message.citations.map((citation) => (
                              <li key={citation.lessonId}>
                                {t("citation", {
                                  title: citation.lessonTitle,
                                })}
                              </li>
                            ))}
                          </ul>
                        ) : null}
                        {message.role === "assistant" && priorUser ? (
                          <button
                            type="button"
                            className="mt-2 rounded border border-[var(--accent)] px-2 py-1 text-xs font-medium text-[var(--accent-ink)] hover:bg-[var(--accent-soft)] disabled:opacity-50"
                            disabled={Boolean(escalatingId) || escalatedIds[message.id]}
                            onClick={() => void handleEscalate(message)}
                            data-testid={`ai-tutor-escalate-${message.id}`}
                          >
                            {escalatedIds[message.id]
                              ? tp("escalated")
                              : escalatingId === message.id
                                ? tp("escalating")
                                : tp("escalate")}
                          </button>
                        ) : null}
                      </div>
                    );
                  })
                )}
              </div>

              <form className="mt-4 flex gap-2" onSubmit={(event) => void handleSend(event)}>
                <input
                  type="text"
                  value={draft}
                  onChange={(event) => setDraft(event.target.value)}
                  placeholder={t("placeholder")}
                  className="flex-1 rounded border border-[var(--border-strong)] px-3 py-2 text-sm"
                  maxLength={COMMS_LIMITS.aiTutorQuestion}
                  data-testid="ai-tutor-input"
                />
                <button
                  type="submit"
                  disabled={sending || !draft.trim()}
                  className="rounded bg-[var(--ink-9)] px-3 py-2 text-sm text-white disabled:opacity-50"
                  data-testid="ai-tutor-send"
                >
                  {sending ? t("sending") : t("send")}
                </button>
              </form>
            </>
          )}
        </>
      ) : null}
    </section>
  );
}
